import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead,
} from "@amzn/meridian/data-table";
import Input from "@amzn/meridian/input";
import Tag from "@amzn/meridian/tag";
import Divider from "@amzn/meridian/divider";

import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchIndirectStaffingConfig,
  updateIndirectStaffingConfig,
} from "../../../store/slices/settingsSlice";
import { selectStationCode, setStationCode } from "../../../store/slices/shiftPlanSlice";
import { showError, showNotification, selectHasErrors } from "../../../store/slices/errorSlice";
import { flattenRules, buildChangePayload } from "../utils";
import {
  FlattenedRule,
  IndirectStaffingChange,
  DraftEntry,
  DraftStaffingFunction,
} from "../types";
import {
  getIndirectStaffingConfig,
  listConfigDrafts,
  promoteConfig,
} from "../api";
import UpdateIndirectHCModal from "./UpdateIndirectHCModal";

const TABLE_COLUMNS = [
  "Department", "Process Path", "Function", "Direct Role Mapping",
  "Description", "Rule Type", "Rule Applies On",
  "Direct Base Ratio", "Indirect Req. Ratio", "Actions",
];

const DRAFT_TABLE_COLUMNS = [
  "Action", "Department", "Process Path", "Function", "Direct Role Mapping",
  "Description", "Rule Type", "Rule Applies On",
  "Direct Base Ratio", "Indirect Req. Ratio",
];

const getStatusTagType = (action?: string): "success" | "warning" | "error" | undefined => {
  if (action === "CREATE") return "success";
  if (action === "UPDATE" || action === "UPSERT") return "warning";
  if (action === "DELETE") return "error";
  return undefined;
};

const getCellBgColor = (action?: string): string | undefined => {
  if (action === "CREATE") return "#e6f9e6";
  if (action === "UPDATE" || action === "UPSERT") return "#fff8e1";
  if (action === "DELETE") return "#fde8e8";
  return undefined;
};

// TODO: [Tech Debt] Extract DraftManagementBar (stage version search, View Drafts, Promote)
// into a shared component used by both IndirectHCTab and FlowPathPercentTab.
// See: https://taskei.amazon.dev/rooms/4d9150b8-4d14-4e72-892c-e82220aa16eb/overview
const IndirectHCTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const rules = useAppSelector((state) => state.settings.indirectHC.rules);
  const loading = useAppSelector((state) => state.settings.indirectHC.loading);
  const error = useAppSelector((state) => state.settings.indirectHC.error);
  const saving = useAppSelector((state) => state.settings.indirectHC.saving);
  const stationCode = useAppSelector(selectStationCode);
  const hasNotifications = useAppSelector(selectHasErrors);

  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<FlattenedRule | null>(null);
  const [serverValidationErrors, setServerValidationErrors] = useState<string[]>([]);
  const pendingRefresh = useRef(false);
  const [pendingChanges, setPendingChanges] = useState<IndirectStaffingChange[]>([]);
  const [infoBanner, setInfoBanner] = useState<string | null>(null);

  // Search / draft preview state
  const [searchVersion, setSearchVersion] = useState("");
  const [draftPreview, setDraftPreview] = useState<DraftStaffingFunction[] | null>(null);
  const [draftVersionName, setDraftVersionName] = useState<string | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);

  // Drafts list state
  const [drafts, setDrafts] = useState<DraftEntry[]>([]);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsLoading, setDraftsLoading] = useState(false);

  // Promote state
  const [promoting, setPromoting] = useState(false);

  // Fallback: read stationCode from URL query params
  useEffect(() => {
    if (!stationCode) {
      const searchParam = new URLSearchParams(window.location.search);
      const stationCodeParam = searchParam.get("stationCode");
      if (stationCodeParam) {
        dispatch(setStationCode({ stationCode: stationCodeParam }));
      }
    }
  }, [dispatch, stationCode]);

  useEffect(() => {
    if (stationCode) {
      dispatch(fetchIndirectStaffingConfig(stationCode));
    }
  }, [dispatch, stationCode]);

  // Re-fetch table data after the user dismisses the success notification
  useEffect(() => {
    if (!hasNotifications && pendingRefresh.current && stationCode) {
      pendingRefresh.current = false;
      dispatch(fetchIndirectStaffingConfig(stationCode));
    }
  }, [hasNotifications, dispatch, stationCode]);

  const flatRows = useMemo(() => flattenRules(rules), [rules]);

  // Merge live rows with pending changes for display
  const displayRows = useMemo((): (FlattenedRule & { _changeAction?: string })[] => {
    if (pendingChanges.length === 0) return flatRows;

    const rows: (FlattenedRule & { _changeAction?: string })[] = [];
    const deletedKeys = new Set<string>();
    const updatedKeys = new Map<string, IndirectStaffingChange>();

    for (const change of pendingChanges) {
      const key = `${change.department}|${change.processPath}|${change.function}`;
      if (change.action === "DELETE") deletedKeys.add(key);
      else updatedKeys.set(key, change);
    }

    // Existing rows — mark updated or deleted
    for (const row of flatRows) {
      const key = `${row.department}|${row.processPath}|${row.function}`;
      if (deletedKeys.has(key)) {
        rows.push({ ...row, _changeAction: "DELETE" });
      } else if (updatedKeys.has(key)) {
        const change = updatedKeys.get(key)!;
        const rule = change.ruleList?.[0];
        rows.push({
          ...row,
          directRoleMapping: change.directRoleMapping || row.directRoleMapping,
          description: change.description || row.description,
          priority: rule?.priority ?? row.priority,
          ruleType: rule?.ruleType ?? row.ruleType,
          ruleAppliesOn: rule?.ruleAppliesOn ?? row.ruleAppliesOn,
          directBaseRatio: rule?.directBaseRatio ?? row.directBaseRatio,
          indirectRequirementRatio: rule?.indirectRequirementRatio ?? row.indirectRequirementRatio,
          lever: rule?.lever ?? row.lever,
          _changeAction: "UPDATE",
        });
      } else {
        rows.push(row);
      }
    }

    // New CREATE rows
    for (const change of pendingChanges) {
      if (change.action === "CREATE" && change.ruleList) {
        for (const rule of change.ruleList) {
          rows.push({
            department: change.department,
            processPath: change.processPath,
            function: change.function,
            directRoleMapping: change.directRoleMapping || "",
            description: change.description || "",
            priority: rule.priority,
            ruleType: rule.ruleType,
            ruleAppliesOn: rule.ruleAppliesOn,
            directBaseRatio: rule.directBaseRatio,
            indirectRequirementRatio: rule.indirectRequirementRatio,
            lever: rule.lever,
            _changeAction: "CREATE",
          });
        }
      }
    }
    return rows;
  }, [flatRows, pendingChanges]);

  const hasPendingChanges = pendingChanges.length > 0;

  // --- Handlers ---
  const handleAdd = () => { setEditData(null); setModalOpen(true); };

  const handleEdit = (row: FlattenedRule) => { setEditData(row); setModalOpen(true); };

  const handleLocalSave = (change: IndirectStaffingChange) => {
    const key = `${change.department}|${change.processPath}|${change.function}`;
    setPendingChanges((prev) => [
      ...prev.filter((c) => `${c.department}|${c.processPath}|${c.function}` !== key),
      change,
    ]);
    setModalOpen(false);
    setEditData(null);
  };

  const handleLocalDelete = (row: FlattenedRule) => {
    const change = buildChangePayload("DELETE", row as any, row.ruleType);
    const key = `${change.department}|${change.processPath}|${change.function}`;
    setPendingChanges((prev) => [
      ...prev.filter((c) => `${c.department}|${c.processPath}|${c.function}` !== key),
      change,
    ]);
  };

  const handleDiscard = () => {
    setPendingChanges([]);
    setInfoBanner(null);
  };

  const handlePublish = async () => {
    if (pendingChanges.length === 0 || !stationCode) return;
    setServerValidationErrors([]);
    try {
      const result = await dispatch(
        updateIndirectStaffingConfig({
          warehouseId: stationCode,
          updatedBy: "",
          changes: pendingChanges,
        })
      ).unwrap();

      if (result.status === "PROMOTED") {
        pendingRefresh.current = true;
        dispatch(showNotification({ message: result.message, type: "success" }));
        setPendingChanges([]);
        setInfoBanner(null);
      } else if (result.status === "PENDING_APPROVAL") {
        setInfoBanner(
          `Draft saved (${result.stageVersionName}). Requires approval from an authorized user.`
        );
        setPendingChanges([]);
      } else if (result.status === "FAILED") {
        const errMsg = result.validationErrors
          ?.map((e) => `${e.function}: ${e.error}`)
          .join("; ") || result.message;
        dispatch(showError({ message: errMsg }));
        if (result.validationErrors) {
          setServerValidationErrors(result.validationErrors.map((e) => `${e.function}: ${e.error}`));
        }
      }
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to publish changes" }));
    }
  };

  // --- Search / Draft Preview ---
  const handleSearch = async (versionOverride?: string) => {
    if (!stationCode) return;
    const version = versionOverride ?? searchVersion;
    setDraftLoading(true);
    try {
      const resp = await getIndirectStaffingConfig(stationCode, version || undefined);
      if (version) {
        setDraftPreview(resp.indirectStaffingRules as DraftStaffingFunction[]);
        setDraftVersionName(version);
      } else {
        setDraftPreview(null);
        setDraftVersionName(null);
        dispatch(fetchIndirectStaffingConfig(stationCode));
      }
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to fetch config" }));
      setDraftPreview(null);
      setDraftVersionName(null);
    } finally {
      setDraftLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchVersion("");
    setDraftPreview(null);
    setDraftVersionName(null);
  };

  // --- Drafts List ---
  const handleViewDrafts = async () => {
    if (!stationCode) return;
    setDraftsLoading(true);
    try {
      const resp = await listConfigDrafts(stationCode, "INDIRECT_STAFFING");
      setDrafts(resp.drafts || []);
      setDraftsOpen(true);
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to list drafts" }));
    } finally {
      setDraftsLoading(false);
    }
  };

  const handleSelectDraft = (versionName: string) => {
    setSearchVersion(versionName);
    setDraftsOpen(false);
    handleSearch(versionName);
  };

  // --- Promote ---
  const handlePromote = async () => {
    if (!stationCode || !draftVersionName) return;
    setPromoting(true);
    try {
      const resp = await promoteConfig({
        warehouseId: stationCode,
        configType: "INDIRECT_STAFFING",
        stageVersionName: draftVersionName,
        promotedBy: "",
      });
      dispatch(showNotification({ message: resp.message, type: "success" }));
      handleClearSearch();
      // Refresh live data after promote
      dispatch(fetchIndirectStaffingConfig(stationCode));
    } catch (err: any) {
      dispatch(showError({ message: err.message || "Failed to promote" }));
    } finally {
      setPromoting(false);
    }
  };

  if (!stationCode) {
    return (
      <Box spacingInset="400">
        <Alert type="neutral">
          Please select a warehouse to view indirect staffing configuration.
        </Alert>
      </Box>
    );
  }

  // --- Draft preview mode ---
  if (draftPreview !== null) {
    return (
      <Box spacingInset="400">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Heading level={3} type="h300">Draft Preview: {draftVersionName}</Heading>
          <Row spacing="300">
            <Button type="tertiary" onClick={handleClearSearch}>Back to Live</Button>
            <Button type="primary" onClick={handlePromote} disabled={promoting}>
              {promoting ? "Promoting..." : "Promote"}
            </Button>
          </Row>
        </Row>
        <Box spacingInset="300 none">
          <DataTable showRowDividers>
            <DataTableHead>
              <DataTableRow>
                {DRAFT_TABLE_COLUMNS.map((col) => (
                  <DataTableHeader key={col}>{col}</DataTableHeader>
                ))}
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {draftPreview.map((fn, fi) => {
                const action = (fn as DraftStaffingFunction).action || "";
                if (action === "DELETE") {
                  return (
                    <DataTableRow key={`draft-${fi}`}>
                      <DataTableCell backgroundColor="#fde8e8">
                        <Tag type="error">{action}</Tag>
                      </DataTableCell>
                      <DataTableCell backgroundColor="#fde8e8">{fn.department}</DataTableCell>
                      <DataTableCell backgroundColor="#fde8e8">{fn.processPath}</DataTableCell>
                      <DataTableCell backgroundColor="#fde8e8">{fn.function}</DataTableCell>
                      <DataTableCell backgroundColor="#fde8e8" colSpan={8}>—</DataTableCell>
                    </DataTableRow>
                  );
                }
                return (fn.ruleList || []).map((rule, ri) => {
                  const bgColor = getCellBgColor(action);
                  const tagType = getStatusTagType(action);
                  return (
                    <DataTableRow key={`draft-${fi}-${ri}`}>
                      <DataTableCell backgroundColor={bgColor}>
                        {tagType ? <Tag type={tagType}>{action}</Tag> : action}
                      </DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{fn.department}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{fn.processPath}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{fn.function}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{fn.directRoleMapping}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{fn.description}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{rule.ruleType}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{rule.ruleAppliesOn}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{rule.directBaseRatio ?? "—"}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{rule.indirectRequirementRatio ?? "—"}</DataTableCell>
                    </DataTableRow>
                  );
                });
              })}
            </DataTableBody>
          </DataTable>
        </Box>
      </Box>
    );
  }

  // --- Main live view ---
  return (
    <Box spacingInset="400">
      {/* Search bar */}
      <Row spacing="300" alignmentVertical="bottom">
        <Input
          value={searchVersion}
          onChange={(v: string) => setSearchVersion(v)}
          label="Stage Version (optional)"
          placeholder="e.g. Stage Version Name"
          size="medium"
        />
        <Button type="secondary" onClick={() => handleSearch()} disabled={draftLoading}>
          {draftLoading ? "Searching..." : "Search"}
        </Button>
        <Button type="tertiary" onClick={draftsOpen ? () => setDraftsOpen(false) : handleViewDrafts} disabled={draftsLoading}>
          {draftsLoading ? "Loading..." : draftsOpen ? "Close Drafts" : "View Drafts"}
        </Button>
      </Row>

      {/* Drafts dropdown */}
      {draftsOpen && (
        <Box spacingInset="300 none">
          <Alert type="neutral">
            <Text type="b200">Pending Drafts:</Text>
            {drafts.length === 0 ? (
              <Text>No pending drafts.</Text>
            ) : (
              drafts.map((d) => (
                <Row key={d.stageVersionName} spacing="300" alignmentVertical="center">
                  <Text>{d.stageVersionName} — by {d.createdBy} ({d.changeCount} changes)</Text>
                  <Button type="tertiary" size="small" onClick={() => handleSelectDraft(d.stageVersionName)}>
                    Preview
                  </Button>
                </Row>
              ))
            )}
          </Alert>
        </Box>
      )}

      {/* Info banner for pending approval */}
      {infoBanner && (
        <Box spacingInset="300 none">
          <Alert type="neutral">
            <Row alignmentHorizontal="justify" alignmentVertical="center">
              <Text>{infoBanner}</Text>
              <Button type="tertiary" size="small" onClick={() => setInfoBanner(null)}>Ack</Button>
            </Row>
          </Alert>
        </Box>
      )}

      {/* Header + action buttons */}
      <Box spacingInset="400 none">
        <Divider />
      </Box>
      <Box spacingInset="300 none">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Heading level={3} type="h300">
            Indirect Staffing Rules
            {hasPendingChanges && <Text color="secondary"> ({pendingChanges.length} pending)</Text>}
          </Heading>
          <Row spacing="300">
            <Button type="primary" onClick={handleAdd} disabled={loading}>Add</Button>
            {hasPendingChanges && (
              <>
                <Button type="primary" onClick={handlePublish} disabled={saving}>
                  {saving ? "Publishing..." : "Publish"}
                </Button>
                <Button type="tertiary" onClick={handleDiscard}>Discard Changes</Button>
              </>
            )}
          </Row>
        </Row>
      </Box>

      {error && (
        <Box spacingInset="300 none">
          <Alert type="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box spacingInset="500">
          <Text color="secondary">Loading indirect staffing configuration...</Text>
        </Box>
      ) : (
        <Box spacingInset="300 none">
          <DataTable showRowDividers>
            <DataTableHead>
              <DataTableRow>
                {hasPendingChanges && <DataTableHeader>Status</DataTableHeader>}
                {TABLE_COLUMNS.map((col) => (
                  <DataTableHeader key={col}>{col}</DataTableHeader>
                ))}
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {displayRows.length === 0 ? (
                <DataTableRow>
                  <DataTableCell colSpan={TABLE_COLUMNS.length + (hasPendingChanges ? 1 : 0)}>
                    <Text color="secondary">No rules configured.</Text>
                  </DataTableCell>
                </DataTableRow>
              ) : (
                displayRows.map((row, idx) => {
                  const bgColor = getCellBgColor(row._changeAction);
                  const tagType = getStatusTagType(row._changeAction);
                  return (
                    <DataTableRow key={`${row.department}-${row.processPath}-${row.function}-${idx}`}>
                      {hasPendingChanges && (
                        <DataTableCell backgroundColor={bgColor}>
                          {row._changeAction
                            ? <Tag type={tagType!}>{row._changeAction}</Tag>
                            : "—"}
                        </DataTableCell>
                      )}
                      <DataTableCell backgroundColor={bgColor}>{row.department}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.processPath}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.function}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.directRoleMapping}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.description}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.ruleType}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.ruleAppliesOn}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.directBaseRatio ?? "—"}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>{row.indirectRequirementRatio ?? "—"}</DataTableCell>
                      <DataTableCell backgroundColor={bgColor}>
                        {row._changeAction !== "DELETE" && (
                          <Row spacing="200">
                            <Button type="tertiary" size="small" onClick={() => handleEdit(row)}>Edit</Button>
                            <Button type="tertiary" size="small" onClick={() => handleLocalDelete(row)}>Delete</Button>
                          </Row>
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })
              )}
            </DataTableBody>
          </DataTable>
        </Box>
      )}

      <UpdateIndirectHCModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditData(null); }}
        onSave={handleLocalSave}
        editData={editData}
        isSaving={false}
        serverValidationErrors={serverValidationErrors}
      />
    </Box>
  );
};

export default IndirectHCTab;
